import React, { useMemo, useState } from "react";
import HoverChat from "../../components/HoverChat";
import Action from "../../components/Action";
import { ASSUMPTIONS, computeAudit } from "./calc";
import { primaryButtonStyle } from "./StepEnergy";
import RfpForm from "./RfpForm";
import GcppModel from "./GcppModel";
import UnderThreshold from "./UnderThreshold";

const inr = (n) =>
  n === undefined || n === null || !isFinite(n)
    ? "—"
    : "₹" +
      Math.round(n)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))(?=(\d{2,})*\d{3})/g, ",");

/**
 * Step 3 — Instant output.
 *
 * Renders the honest math (system size, payback, diesel savings, landed cost),
 * enforces the ≥250 kWh gate, and offers either the RFP path or the "co-own
 * a share" reframe depending on the computed system size.
 */
export default function StepResults({ values, onBack, onEvent }) {
  const result = useMemo(() => computeAudit(values), [values]);
  const [rfpOpen, setRfpOpen] = useState(false);
  const [gcppOpen, setGcppOpen] = useState(
    values.loadProfile === "captive-seeker"
  );

  const underThreshold = result.systemSizeKwh < 250;

  return (
    <div data-testid="audit-step-results">
      <div className="gg-eyebrow" style={{ marginBottom: 10 }}>
        Step 3 of 3 · Result
      </div>
      <h2 className="gg-h1" style={{ maxWidth: "22ch" }}>
        Here's what a{" "}
        <span style={{ color: "var(--gold-soft)" }}>
          {result.systemType === "SB" ? "Solar + BESS" : "BESS-only"}
        </span>{" "}
        system on your site could do.
      </h2>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 16,
          marginTop: 32,
        }}
        data-testid="audit-results-grid"
      >
        <Metric
          testid="metric-system"
          label={result.systemType === "SB" ? "Solar + BESS" : "BESS-only"}
          value={
            result.systemType === "SB"
              ? `${result.solarKwp} kWp solar · ${result.bessKwh} kWh BESS`
              : `${result.bessKwh} kWh BESS`
          }
          note={
            <>
              Chemistry: <HoverChat term="lfp">LFP</HoverChat>, watched cell-by-cell by our{" "}
              <HoverChat term="bms">BMS</HoverChat>.
            </>
          }
        />
        <Metric
          testid="metric-payback"
          label="Indicative payback"
          value={
            result.paybackYears === Infinity
              ? "—"
              : `${result.paybackYears} yrs`
          }
          note="On projected annual savings, before financing."
        />
        <Metric
          testid="metric-diesel"
          label="Diesel eliminated"
          value={
            result.dieselLitresSaved > 0
              ? `${result.dieselLitresSaved.toLocaleString("en-IN")} L / yr`
              : "No DG in scope"
          }
          note={
            result.dieselLitresSaved > 0
              ? `${inr(result.dieselInrSaved)} / yr in fuel + logistics avoided.`
              : "Add DG hours in Step 1 if you burn diesel."
          }
        />
        <Metric
          testid="metric-landed"
          label="Landed cost of energy"
          value={
            <>
              <span style={{ color: "var(--text-mute)", textDecoration: "line-through" }}>
                ₹{result.currentLandedInrPerKwh}/kWh
              </span>
              {"  →  "}
              <span style={{ color: "var(--gold-soft)" }}>
                ₹{result.projectedLandedInrPerKwh}/kWh
              </span>
            </>
          }
          note={
            <>
              For a <HoverChat term="gcpp">Group Captive</HoverChat> structure the
              floor is around ₹4/kWh.
            </>
          }
        />
      </div>

      {/* Honest math footnote */}
      <details
        data-testid="audit-assumptions"
        style={{
          marginTop: 26,
          padding: "12px 16px",
          borderRadius: 10,
          border: "1px dashed var(--line)",
          color: "var(--text-mute)",
          fontSize: 13,
          lineHeight: 1.55,
        }}
      >
        <summary
          style={{
            cursor: "pointer",
            color: "var(--text-dim)",
            listStyle: "revert",
          }}
        >
          Assumptions behind these numbers (honest math)
        </summary>
        <ul style={{ margin: "10px 0 0 0", padding: "0 0 0 20px" }}>
          <li>
            Solar CUF {ASSUMPTIONS.solarCUF * 100}% (India average — location-
            dependent, refined at RFP stage).
          </li>
          <li>
            LFP battery — 10-year warranted life, 2% annual degradation,{" "}
            {ASSUMPTIONS.bessDischargeHours}h discharge default.
          </li>
          <li>
            Solar install ₹{ASSUMPTIONS.solarInstallCostPerKwp.toLocaleString("en-IN")}/kWp;
            BESS cost tiered from ₹22,000/kWh (small) to ₹12,000/kWh (≥1 MWh).
          </li>
          <li>
            Payback treats BESS arbitrage at 40% of the flat tariff — conservative
            vs. peak-shaving reality; will refine at RFP stage.
          </li>
          <li>
            These figures are <em>indicative, not quoted.</em> A full engineered
            proposal follows an RFP.
          </li>
        </ul>
      </details>

      {/* Gate: ≥250 kWh → RFP; <250 → reserve-a-block reframe */}
      {underThreshold ? (
        <UnderThreshold
          result={result}
          data-testid="audit-under-threshold-panel"
        />
      ) : (
        <>
          <div
            style={{
              marginTop: 34,
              padding: "22px 24px",
              borderRadius: "var(--radius-lg)",
              background:
                "linear-gradient(180deg, rgba(244,169,31,0.09), rgba(244,169,31,0.02))",
              border: "1px solid rgba(244,169,31,0.35)",
            }}
            data-testid="audit-rfp-cta-panel"
          >
            <p
              style={{
                color: "var(--text-cloud)",
                fontSize: 16,
                lineHeight: 1.7,
                margin: 0,
              }}
            >
              Your site clears the ≥250 kWh floor at a{" "}
              <strong>{result.systemSizeKwh} kWh</strong> BESS — ready for a real
              engineering conversation. You can{" "}
              <Action
                route="submit-rfp"
                sourceCell="audit"
                sourceState="step-3"
                onClick={() => setRfpOpen(true)}
              >
                submit an RFP to Green Gold Engineering
              </Action>{" "}
              in a moment. If you're pursuing captive ownership, you can also{" "}
              <Action
                route="gcpp-model"
                sourceCell="audit"
                sourceState="step-3"
                onClick={() => {
                  setGcppOpen(true);
                  setTimeout(() => {
                    document
                      .querySelector('[data-testid="audit-gcpp-panel"]')
                      ?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }, 50);
                }}
              >
                explore the GCPP savings model
              </Action>
              .
            </p>
          </div>

          {gcppOpen && (
            <GcppModel
              baseValues={values}
              onSubmitRfp={() => setRfpOpen(true)}
              onEvent={onEvent}
            />
          )}

          {rfpOpen && (
            <RfpForm
              values={values}
              result={result}
              variant={values.loadProfile === "captive-seeker" ? "gcpp" : result.systemType.toLowerCase()}
              onClose={() => setRfpOpen(false)}
              onEvent={onEvent}
            />
          )}
        </>
      )}

      <div
        style={{
          marginTop: 40,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <button
          type="button"
          onClick={onBack}
          data-testid="audit-back-3"
          style={{
            ...primaryButtonStyle(false),
            background: "transparent",
            color: "var(--text-dim)",
            borderColor: "var(--line)",
            boxShadow: "none",
          }}
        >
          ← Refine my inputs
        </button>
      </div>
    </div>
  );
}

function Metric({ testid, label, value, note }) {
  return (
    <div
      className="gg-glass"
      data-testid={testid}
      style={{
        padding: 18,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div
        style={{
          fontSize: 10.5,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          color: "var(--text-mute)",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: "Fraunces, Georgia, serif",
          fontSize: 22,
          lineHeight: 1.2,
          color: "var(--paper)",
        }}
      >
        {value}
      </div>
      {note && (
        <div style={{ fontSize: 12.5, color: "var(--text-dim)", lineHeight: 1.5 }}>
          {note}
        </div>
      )}
    </div>
  );
}
